/**
 * HUB-D — Carte session caisse (responsive, DS Remobiles).
 */
import React from 'react';
import { FileText, Lock } from 'lucide-react';
import MagasinBadge from '../MagasinBadge';
import {
  ecartClass,
  formatCaisseDateTime,
  formatEuro,
  sessionStatusMeta,
} from '../../utils/caisse/caisseDisplay';

function StatRow({ label, value, valueClass = 'text-[var(--rm-white)]' }) {
  return (
    <div className="flex justify-between gap-2 text-sm">
      <span className="text-[var(--rm-gray-500)]">{label}</span>
      <span className={`font-semibold ${valueClass}`}>{value}</span>
    </div>
  );
}

export default function ActiviteCaisseSessionCard({
  caisse,
  actions,
  isAdmin,
  forceClosingId,
  testIdPrefix = 'activite-caisse',
}) {
  const status = sessionStatusMeta(caisse);
  const canForceClose = !caisse.heure_fermeture && isAdmin;

  return (
    <article
      className="rounded-xl border p-4 space-y-3"
      style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}
      data-testid={`${testIdPrefix}-session-${caisse.id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-bold text-[var(--rm-white)]">{caisse.date_caisse}</span>
          <MagasinBadge code={caisse.magasin_id || 'M1'} size="sm" />
          <span className={status.badge}>{status.label}</span>
        </div>
        <div className={`font-bold text-lg ${ecartClass(caisse.ecart)}`}>
          {formatEuro(caisse.ecart)}
          <span className="text-xs font-normal text-[var(--rm-gray-500)] ml-1">écart</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <StatRow label="Ouverture" value={formatCaisseDateTime(caisse.heure_ouverture)} valueClass="text-[var(--rm-gray-300)]" />
        <StatRow label="Fermeture" value={formatCaisseDateTime(caisse.heure_fermeture)} valueClass="text-[var(--rm-gray-300)]" />
        <StatRow label="Fond caisse" value={formatEuro(caisse.fond_caisse)} />
        <StatRow label="Espèces" value={formatEuro(caisse.total_especes)} valueClass="text-[var(--rm-success)]" />
        <StatRow label="CB" value={formatEuro(caisse.total_cb)} />
        <StatRow label="Stripe" value={formatEuro(caisse.total_stripe)} />
        <StatRow label="Ventes" value={formatEuro(caisse.total_ventes)} valueClass="text-[var(--rm-success)]" />
        <StatRow label="Retraits" value={formatEuro(caisse.total_retraits)} valueClass="text-[var(--rm-red-light)]" />
        <StatRow label="Théorique" value={formatEuro(caisse.total_theorique)} />
        <StatRow label="Dépôt coffre" value={formatEuro(caisse.montant_coffre)} />
        <StatRow label="Ouvert par" value={caisse.opened_by || caisse.user_ouverture || '—'} valueClass="text-[var(--rm-gray-300)]" />
        <StatRow label="Fermé par" value={caisse.closed_by || caisse.user_fermeture || '—'} valueClass="text-[var(--rm-gray-300)]" />
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="button"
          onClick={() => actions.printSession(caisse.id)}
          className="btn-secondary px-4 py-2.5 min-h-[44px] text-sm inline-flex items-center gap-2"
          data-testid={`${testIdPrefix}-print-${caisse.id}`}
        >
          <FileText className="w-4 h-4" />
          Imprimer
        </button>
        {canForceClose && (
          <button
            type="button"
            onClick={() => actions.forceClose(caisse.id, caisse.date_caisse)}
            disabled={forceClosingId === caisse.id}
            className="px-4 py-2.5 min-h-[44px] text-sm inline-flex items-center gap-2 rounded-xl font-semibold border disabled:opacity-50"
            style={{ borderColor: 'var(--rm-border-strong)', background: 'var(--rm-red-muted)', color: 'var(--rm-red-light)' }}
            data-testid={`${testIdPrefix}-force-close-${caisse.id}`}
          >
            <Lock className="w-4 h-4" />
            {forceClosingId === caisse.id ? 'Fermeture…' : 'Fermer (forcé)'}
          </button>
        )}
      </div>
    </article>
  );
}
